/**
 * Reading the anchor a citation was stored with.
 *
 * WHAT AN ANCHOR IS FOR
 *
 * A citation reaches a viewer as text, and the viewer finds it by searching the
 * rendered document. That search cannot be made reliable, because the text the
 * backend stored and the text the renderer extracts are two different
 * renderings of one file. So a citation on a long passage matches on its
 * opening and highlights the wrong sentence, and a citation on a table row
 * matches on token overlap and highlights the wrong row.
 *
 * An anchor names the place instead of describing the text, so there is nothing
 * to search for.
 *
 * WHY THIS IS DEFENSIVE TO THE POINT OF PARANOIA
 *
 * Everything here comes off a search index by way of the network, written by a
 * backend that may be older or newer than this build, and re-indexing can leave
 * a document carrying a shape neither side expects. The worst outcome is not a
 * missing highlight, it is a highlight drawn confidently over the wrong part of
 * a document, which a reader has no way to recognise as wrong. So every field
 * is checked, anything unrecognised yields nothing, and yielding nothing simply
 * returns the viewer to the text search it used before anchors existed.
 */

import { logger } from "@/telemetry";
import { Citation, PdfCitationBox } from "@/types/citations";

/** The kinds a backend may store. Only `pdf` is consumed today. */
const KIND_PDF = "pdf";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * The boxes a PDF citation was stored with, or undefined when it has none.
 *
 * Undefined rather than an empty array, so that "no usable anchor" has one
 * representation and the viewer's optional prop reads as genuinely absent. The
 * viewer falls back to searching the text in either case, so the rule keeps a
 * single canonical shape rather than changing behaviour.
 *
 * Rejections of a pdf anchor are logged, because a pdf anchor that yields no
 * boxes is evidence of a backend storing a shape this build does not expect,
 * and a silent fallback would show up in production only as highlighting
 * quietly getting worse. Absence is not logged, since every document ingested
 * before anchors existed and every chat upload lacks one.
 */
export function pdfBoxesFor(citation: Citation | null | undefined): PdfCitationBox[] | undefined {
  const anchor = citation?.metadata?.citation_anchor;

  if (!anchor || typeof anchor !== "object") return undefined;

  const record = anchor as Record<string, unknown>;

  if (record.kind !== KIND_PDF) return undefined;

  const documentId = citation?.document_id;
  const stored = record.boxes;

  if (!Array.isArray(stored)) {
    logger.warn("Citation anchor rejected: pdf anchor without a boxes array", {
      documentId,
      boxesType: typeof stored,
    });

    return undefined;
  }

  const boxes: PdfCitationBox[] = [];

  for (const raw of stored) {
    if (!raw || typeof raw !== "object") continue;

    const { page, left, top, width, height } = raw as Record<string, unknown>;

    // Pages are 1-indexed integers. A fractional page would key a plan that no
    // rendered page can match and index no element in the viewer.
    if (!isFiniteNumber(page) || !Number.isInteger(page) || page < 1) continue;
    if (!isFiniteNumber(left) || !isFiniteNumber(top)) continue;
    // A box of no size marks nothing, and a negative one marks a rectangle the
    // browser resolves in its own way; neither is worth drawing.
    if (!isFiniteNumber(width) || width <= 0) continue;
    if (!isFiniteNumber(height) || height <= 0) continue;

    boxes.push({ page, left, top, width, height });
  }

  if (boxes.length < stored.length) {
    logger.warn("Citation anchor dropped malformed boxes", {
      documentId,
      stored: stored.length,
      kept: boxes.length,
    });
  }

  return boxes.length > 0 ? boxes : undefined;
}
